import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Typography, Alert, Row, Col } from 'antd';
import { useCalculatorState } from './hooks/useCalculatorState';
import { useCalculation } from './hooks/useCalculation';
import CalculatorForm from './components/CalculatorForm';
import ResultsDisplay from './components/ResultsDisplay';
import CalculatorTable from './components/CalculatorTable';
import CalculatorChart from './components/CalculatorChart';
import { useTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from './theme';
import useMediaQuery from '@mui/material/useMediaQuery';

const { Title } = Typography;

function Calculator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const prevCalculationType = useRef(null);
  const [showResults, setShowResults] = useState(false);

  const {
    balance,
    interestRate,
    monthlyContribution,
    additionalSpending,
    calculationType,
    years,
    months,
    targetMonths,
    targetEndBalance,
    currency,
    contributionMonths,
    annualFeeRate,
    startDate,
    mortgageAmount,
    fixedRate,
    fixedTerm,
    totalTerm,
    variableRate,
    deposit,
    mortgageType,
    propertyValue,
    mortgageStartMonth,
    mortgageStartYear,
    setMortgageStartMonth,
    setMortgageStartYear,
    setPropertyValue,
    monthlyOverpayment,
    lumpSumOverpayments,
    overpaymentStrategy,
    setMonthlyOverpayment,
    setLumpSumOverpayments,
    setOverpaymentStrategy,
    setCalculationTypeAndReset,
    setCurrency,
    setMonthlyContribution,
    setBalance,
    setInterestRate,
    setAdditionalSpending,
    setTargetMonths,
    setContributionMonths,
    setTargetEndBalance,
    setYears,
    setMonths,
    setAnnualFeeRate,
    setMortgageAmount,
    setFixedRate,
    setFixedTerm,
    setTotalTerm,
    setVariableRate,
    setDeposit,
    setMortgageType,
    resetState,
    resetTypeSpecificFields
  } = useCalculatorState();

  const {
    results,
    finalMonth,
    errorMessage,
    inputsChanged,
    pageSize,
    cumulativeTotals,
    calculatePayments,
    currencyFormatter,
    getTableColumns,
    setPageSize,
    setInputsChanged,
    lastCalculatedValues,
    resetCalculation
  } = useCalculation();

  const handleCalculationTypeChange = useCallback((newType) => {
    setShowResults(false);
    resetCalculation();
    setCalculationTypeAndReset(newType);
    setInputsChanged(false);
  }, [resetCalculation, setCalculationTypeAndReset, setInputsChanged]);

  const resetCalculationAndSetInputsChanged = useCallback(() => {
    resetCalculation();
    setInputsChanged(true);
  }, [resetCalculation, setInputsChanged]);

  useEffect(() => {
    // Reset relevant states only when calculation type changes
    if (prevCalculationType.current !== calculationType) {
      resetCalculationAndSetInputsChanged();
      resetTypeSpecificFields(calculationType);
      prevCalculationType.current = calculationType;
    }
  }, [calculationType, resetCalculationAndSetInputsChanged, resetTypeSpecificFields]);

  const handleCalculate = () => {
    setInputsChanged(false);
    calculatePayments({
      balance,
      interestRate,
      monthlyContribution,
      additionalSpending,
      calculationType,
      years,
      months,
      targetMonths,
      targetEndBalance,
      currency,
      contributionMonths,
      annualFeeRate,
      startDate,
      // Add mortgage-specific parameters
      mortgageAmount,
      fixedRate,
      fixedTerm,
      totalTerm,
      variableRate,
      deposit,
      mortgageType,
      mortgageStartMonth,
      mortgageStartYear,
      monthlyOverpayment,
      lumpSumOverpayments,
      overpaymentStrategy
    });
    setShowResults(true);
  };

  const handleReset = () => {
    resetState();
    resetCalculation();
    setInputsChanged(false);
    setShowResults(false);
  };

  const columns = getTableColumns(calculationType, currency);

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    if (results.length > 0) {
      setInputsChanged(true);
    }
  };

  const hasValidResults = results.length > 0 && !inputsChanged && !errorMessage;  

  const renderContent = () => {
    if (isMobile) {
      return (
        <>
          <CalculatorForm
            calculationType={calculationType}
            currency={currency}
            balance={balance}
            interestRate={interestRate}
            monthlyContribution={monthlyContribution}
            additionalSpending={additionalSpending}
            annualFeeRate={annualFeeRate}
            targetMonths={targetMonths}
            targetEndBalance={targetEndBalance}
            contributionMonths={contributionMonths}
            years={years}
            months={months}
            // Mortgage-specific props
            mortgageAmount={mortgageAmount}
            fixedRate={fixedRate}
            fixedTerm={fixedTerm}
            totalTerm={totalTerm}
            variableRate={variableRate}
            deposit={deposit}
            mortgageType={mortgageType}
            propertyValue={propertyValue}
            mortgageStartMonth={mortgageStartMonth}
            mortgageStartYear={mortgageStartYear}

            setCalculationType={handleCalculationTypeChange}
            setCurrency={setCurrency}
            setMonthlyContribution={setMonthlyContribution}
            setBalance={setBalance}
            setInterestRate={setInterestRate}
            setAdditionalSpending={setAdditionalSpending}
            setTargetMonths={setTargetMonths}
            setContributionMonths={setContributionMonths}
            setTargetEndBalance={setTargetEndBalance}
            setYears={setYears}
            setMonths={setMonths}
            setAnnualFeeRate={setAnnualFeeRate}
            // Mortgage-specific setters
            setMortgageAmount={setMortgageAmount}
            setFixedRate={setFixedRate}
            setFixedTerm={setFixedTerm}
            setTotalTerm={setTotalTerm}
            setVariableRate={setVariableRate}
            setDeposit={setDeposit}
            setMortgageType={setMortgageType}
            setMortgageStartMonth={setMortgageStartMonth}
            setMortgageStartYear={setMortgageStartYear}
            setPropertyValue={setPropertyValue}
            monthlyOverpayment={monthlyOverpayment}
            lumpSumOverpayments={lumpSumOverpayments}
            overpaymentStrategy={overpaymentStrategy}
            setMonthlyOverpayment={setMonthlyOverpayment}
            setLumpSumOverpayments={setLumpSumOverpayments}
            setOverpaymentStrategy={setOverpaymentStrategy}

            errorMessage={errorMessage}
            inputsChanged={inputsChanged}
            setInputsChanged={setInputsChanged}
            calculatePayments={handleCalculate}
            handleReset={handleReset}
            handleInputChange={handleInputChange}
          />
          {renderResults()}
        </>
      );
    } else {
      return (
        <>
        <Row gutter={24}>
        <Col span={showResults ? 12 : 24}>
            <CalculatorForm
            calculationType={calculationType}
            currency={currency}
            balance={balance}
            interestRate={interestRate}
            monthlyContribution={monthlyContribution}
            additionalSpending={additionalSpending}
            annualFeeRate={annualFeeRate}
            targetMonths={targetMonths}
            targetEndBalance={targetEndBalance}
            contributionMonths={contributionMonths}
            years={years}
            months={months}
            // Mortgage-specific props
            mortgageAmount={mortgageAmount}
            fixedRate={fixedRate}
            fixedTerm={fixedTerm}
            totalTerm={totalTerm}
            variableRate={variableRate}
            deposit={deposit}
            mortgageType={mortgageType}
            propertyValue={propertyValue}
            mortgageStartMonth={mortgageStartMonth}
            mortgageStartYear={mortgageStartYear}
            monthlyOverpayment={monthlyOverpayment}
            lumpSumOverpayments={lumpSumOverpayments}
            overpaymentStrategy={overpaymentStrategy}
            setMonthlyOverpayment={setMonthlyOverpayment}
            setLumpSumOverpayments={setLumpSumOverpayments}
            setOverpaymentStrategy={setOverpaymentStrategy}

            setCalculationType={handleCalculationTypeChange}
            setCurrency={setCurrency}
            setMonthlyContribution={setMonthlyContribution}
            setBalance={setBalance}
            setInterestRate={setInterestRate}
            setAdditionalSpending={setAdditionalSpending}
            setTargetMonths={setTargetMonths}
            setContributionMonths={setContributionMonths}
            setTargetEndBalance={setTargetEndBalance}
            setYears={setYears}
            setMonths={setMonths}
            setAnnualFeeRate={setAnnualFeeRate}
            // Mortgage-specific setters
            setMortgageAmount={setMortgageAmount}
            setFixedRate={setFixedRate}
            setFixedTerm={setFixedTerm}
            setTotalTerm={setTotalTerm}
            setVariableRate={setVariableRate}
            setDeposit={setDeposit}
            setMortgageType={setMortgageType}
            setMortgageStartMonth={setMortgageStartMonth}
            setMortgageStartYear={setMortgageStartYear}
            setPropertyValue={setPropertyValue}

            errorMessage={errorMessage}
            inputsChanged={inputsChanged}
            setInputsChanged={setInputsChanged}
            calculatePayments={handleCalculate}
            handleReset={handleReset}
            handleInputChange={handleInputChange}
            />
          </Col>
          <Col span={12}>
            {showResults && (
              <>
                <ResultsDisplay
                  calculationType={calculationType}
                  finalMonth={finalMonth}
                  startDate={lastCalculatedValues.startDate}
                  cumulativeTotals={cumulativeTotals}
                  currency={currency}
                  currencyFormatter={currencyFormatter}
                  lastCalculatedValues={lastCalculatedValues}
                />
                <div style={{ marginBottom: '30px', width: '100%', height: 500 }}>
                  <CalculatorChart
                    data={results}
                    calculationType={calculationType}
                    currency={currency}
                  />
                </div>
              </>
            )}
          </Col>
        </Row>
        {showResults && (
            <Row>
              <Col span={24}>
                {inputsChanged && results.length > 0 && (
                  <Alert
                    message="Inputs have changed. Please click 'Calculate' to update the results."
                    type="warning"
                    showIcon
                    style={{ marginBottom: '20px' }}
                  />
                )}
                <CalculatorTable
                  results={results}
                  columns={columns}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                />
              </Col>
            </Row>
          )}
        </>
      );
    }
  };

  const renderResults = () => {
    // Add safety check at the start
    if (!results || !lastCalculatedValues) {
      return null;
    }
  
    return (
      <>
        {inputsChanged && results.length > 0 && (
          <Alert
            message="Inputs have changed. Please click 'Calculate' to update the results."
            type="warning"
            showIcon
            style={{ marginBottom: '20px' }}
          />
        )}
        {hasValidResults && lastCalculatedValues && (
          <>
            <ResultsDisplay
              calculationType={calculationType}
              finalMonth={finalMonth}
              startDate={lastCalculatedValues.startDate}
              cumulativeTotals={cumulativeTotals}
              currency={currency}
              currencyFormatter={currencyFormatter}
              lastCalculatedValues={lastCalculatedValues}
            />
            <div style={{ marginBottom: '30px', width: '100%', height: 400 }}>
              <CalculatorChart
                data={results}
                calculationType={calculationType}
                currency={currency}
              />
            </div>
            <CalculatorTable
              results={results}
              columns={columns}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </>
        )}
      </>
    );
  };

  return (
    <div style={{ width: '100%', margin: '0 auto', padding: '20px', overflowX: 'hidden' }}>
      <Title level={2}>
        {calculationType === 'mortgage' 
          ? 'Mortgage Calculator' 
          : calculationType === 'debt' 
            ? 'Debt Repayment Calculator' 
            : 'Investment Growth Calculator'}
      </Title>
      {renderContent()}
    </div>
  );
}

export default Calculator;